<script lang="ts" setup>
import Breadcrumbs from '@/Layouts/App/Components/Breadcrumbs.vue'
import BaseHeader from '@/Layouts/App/Components/BaseHeader.vue'
import { Modal } from 'inertia-modal'
import CommandPalette from '@/Components/App/Global/CommandPalette.vue'
import { useAppLayout } from '@/stores/appLayout'
import { onMounted, onUnmounted, useTemplateRef } from 'vue'
import NewNotification from '@/Components/App/Communications/NewNotification.vue'
import { NotificationData } from '@/Support/Communications/Interfaces/NotificationData'
import { userData } from '@/Support/Users/userData'
import { storeToRefs } from 'pinia'
import { useNotifications } from '@/stores/notifications'
import { loadLanguageAsync } from 'laravel-vue-i18n'
import { t } from '@/Support/lang'
import AlertModal from '@/Components/App/Modals/AlertModal.vue'
import AiIcon from '@/Components/App/Global/AiIcon.vue'
import SelectTeamables from '@/Components/App/Teams/SelectTeamables.vue'
import { useSelectTeamables } from '@/stores/selectTeamables'
import MiniNotification from '@/Components/App/Global/MiniNotification.vue'
import AIResponseModal from '@/Pages/App/AI/AIResponseModal.vue'

const appLayout = useAppLayout()
const { openCommandPalette, openAiQuery } = appLayout
const { currentLocale, aiResponseModal } = storeToRefs(appLayout)

const { axiosLoader } = storeToRefs(appLayout)
window.axios.interceptors.request.use(config => {
  axiosLoader.value++
  return config
})
window.axios.interceptors.response.use(response => {
  axiosLoader.value--
  return response
})
const keyPress = (event: KeyboardEvent) => {
  switch (true) {
    case (event.key === 'p' && event.ctrlKey) ||
      (event.key === 'm' && event.ctrlKey):
      openCommandPalette()
      break

    case event.key === 'i' && event.ctrlKey:
      openAiQuery()
      break
  }
}
onMounted(() => window.addEventListener('keyup', keyPress))
onUnmounted(() => window.removeEventListener('keyup', keyPress))

loadLanguageAsync(currentLocale.value)

/**
 * Notifications
 */
const notifications = useNotifications()
const { newNotification, loadNotifications } = notifications
const {
  miniNotificationTitle,
  miniNotificationDescription,
  miniNotificationButtonLabel,
  miniNotificationFleeting,
  miniNotificationDuration,
  showMiniNotification,
} = storeToRefs(notifications)
const { selectTeamablesModal } = storeToRefs(useSelectTeamables())
window.Echo.private('App.Models.User.' + userData.value.id).notification(
  (data: NotificationData) => {
    if (!data.hide) {
      newNotification(data)
    }
    loadNotifications()
  },
)

/**
 * AI Response
 */
const aiResponse =
  useTemplateRef<InstanceType<typeof AIResponseModal>>('AI Response')
aiResponseModal.value = aiResponse

const selectTeamables =
  useTemplateRef<InstanceType<typeof SelectTeamables>>('selectTeamables')
selectTeamablesModal.value = selectTeamables
</script>

<template>
  <div class="relative min-h-screen bg-neutral-100 dark:bg-neutral-950">
    <Modal />
    <CommandPalette />
    <BaseHeader />
    <main class="-mt-24 pb-8">
      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
        <h1 class="sr-only">{{ __('Dashboard') }}</h1>
        <!-- Main 3 column grid -->

        <!-- Breadcrumbs -->
        <Breadcrumbs />

        <slot />
      </div>
    </main>
    <footer>
      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 lg:max-w-7xl">
        <div
          class="border-t border-neutral-200 dark:border-neutral-700 py-8 text-sm text-neutral-500 dark:text-neutral-400 text-center sm:text-left"
        >
          <span class="block sm:inline">
            &copy; {{ new Date().getFullYear() }} Ciudad Byte.
          </span>
          <span class="block sm:inline">
            {{ __('All rights reserved.') }}
          </span>
        </div>
      </div>
    </footer>
    <AiIcon />
    <AIResponseModal ref="AI Response" />
    <AlertModal title="Action" />
    <SelectTeamables ref="selectTeamables" />
    <NewNotification />
    <MiniNotification
      v-if="showMiniNotification"
      :title="miniNotificationTitle"
      :context="miniNotificationDescription"
      :button-label="miniNotificationButtonLabel"
      :duration="miniNotificationDuration"
      :fleeting="miniNotificationFleeting"
      class="fixed bottom-0 right-0 w-full sm:w-96 z-10"
      @close="showMiniNotification = false"
    />
  </div>
</template>
